import React from 'react';
import Layout from 'components/layout';
import HeadData from 'components/head-data';
import AgentPage from 'components/agent-search/agent-profile';
import { AGENTS_ROUTE } from 'components/dynamic-page/route-matchers';
import {
  GTM_AGENT_PROFILE_VIEW,
  GTM_AGENT_PROFILE_RETURN_TEXT_CLICK,
  GTM_CLICK_AGENT_PROFILE_COPY_EMAIL,
  GTM_CLICK_AGENT_PROFILE_COPY_PHONE,
  GTM_AGENT_PROFILE_SERVICE_BUTTON_CLICK,
  GTM_AGENT_JOIN_EXP_BUTTON_CLICK,
} from 'constants/events';
import { trackEvent } from 'utils/google-tag-manager';
import { onSocialMediaLinkClick } from 'pages/agents-search/[...dynamic]';
import { createGetServerSideProps, HandlerType } from 'utils/next/shared-getServerSideProps';
import { DynamicPageServerSideProps } from 'types';
import { isTenantExpCommercial, isTenantExpCommercialCa } from '@tenants/utils/which-tenant';
import { NOT_FOUND } from 'constants/not-found';
import { getTenantById } from '@tenants/manager';
import { getTenantOrganizationConfig } from '@tenants/utils/configs';

import type { GetServerSidePropsContext } from 'next';

type DynamicAgentPageProps = DynamicPageServerSideProps<object, typeof AGENTS_ROUTE>;

const handler: HandlerType<DynamicAgentPageProps> = async (context: GetServerSidePropsContext, features, tenant) => {
  const agentInfoFromQuery = context.query.dynamic && context.query.dynamic[0];
  const fullName = agentInfoFromQuery?.split('_')[0];
  const agentName = fullName?.replace('-', ' ');
  const headTags = {
    title: `${agentName} ${tenant.theme.title}`,
    metaDescription: tenant.theme.metaDescription,
  };

  if (!isTenantExpCommercial(tenant.id) && !isTenantExpCommercialCa(tenant.id)) {
    return NOT_FOUND;
  }

  return {
    props: {
      routeName: AGENTS_ROUTE,
      props: {
        headTags,
        features,
        tenantId: tenant.id,
      },
    },
  };
};

export const getServerSideProps = createGetServerSideProps(handler);

const eventTrackers = {
  onPageView: () => trackEvent(GTM_AGENT_PROFILE_VIEW),
  onGoBack: () => trackEvent(GTM_AGENT_PROFILE_RETURN_TEXT_CLICK),
  onSocialMediaLinkClick: onSocialMediaLinkClick,
  onCopyEmailClick: () => trackEvent(GTM_CLICK_AGENT_PROFILE_COPY_EMAIL),
  onCopyPhoneClick: () => trackEvent(GTM_CLICK_AGENT_PROFILE_COPY_PHONE),
  onExploreServicesClick: () => trackEvent(GTM_AGENT_PROFILE_SERVICE_BUTTON_CLICK),
  onJoinExpClick: () => trackEvent(GTM_AGENT_JOIN_EXP_BUTTON_CLICK),
};

export default function DynamicAgentPage({ props }: DynamicAgentPageProps) {
  const { headTags, tenantId } = props;
  const tenant = getTenantById(tenantId);
  const organizationConfig = getTenantOrganizationConfig(tenant);

  return (
    <Layout>
      {headTags && <HeadData data={{ title: headTags.title, metaDescription: headTags.metaDescription }} />}
      <AgentPage eventTrackers={eventTrackers}
        joinUsLink={organizationConfig?.joinSectionUrl}
        isCommercial={isTenantExpCommercial(tenant.id) || isTenantExpCommercialCa(tenant.id)} />
    </Layout>
  );
}